import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import type {SelectOption} from 'src/api/ProactApiClient';
import {
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  NotificationModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {
  useGetCourseDefinitionListQuery,
  useGetCourseDefinitionQuery,
  useUpdateCourseDefinitionMutation,
} from 'src/redux/queries/courseDefinition';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {LockGuard} from 'src/app/lock-mgt/LockGuard';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {CourseDefinitionViewType} from 'src/shared/constants/ViewTypes';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {useUnsavedChangesGuard} from 'src/shared/hooks/useUnsavedChangesGuard';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {CourseFormMeta} from '../../../../app/training/courseFormMeta';
import {getContentLanguages} from '../../../../app/training/languageUtils';
import {
  CoursePillConfig,
  TrainingIcon,
} from '../../../../app/training/coursePills';
import {
  CreateDefaultCourseDefinition,
  MapCourseDefinitionResponseToCourseFormData,
  MapCourseFormDataToCourseDefinitionRequest,
} from '../../../../app/training/courseDefaults';
import {
  TrainingDocumentTitle,
  validateCourseDefinitionContents,
} from '../../../../app/training/courseContentValidation';
import ViewCourseClassrooms from '../shared/viewCourseDefinition/ViewCourseClassrooms';
import ViewCourseDefinitionAssignments from '../shared/viewCourseDefinition/ViewCourseDefinitionAssignments';
import {AssignmentRulesTab} from './components/AssignmentRules/AssignmentRulesTab';
import {BuildTab} from './components/Build/BuildTab';
import {ClassroomOnlyBanner} from './components/ClassroomOnlyBanner';
import {ResultsTab} from './components/ResultsTab';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const ViewCourseDefinitionPage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {definitionId, view} = useParams();

  const trainingPerms = useModulePermissions(ModuleEnum.Training);
  const auditPerms = useAuditLogPermissions();

  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [courses, setCourses] = useState<SelectOption[]>([]);

  const {data: courseListData, error: courseListError} =
    useGetCourseDefinitionListQuery({start: 0, length: 100});

  const {
    data: fetchedDefinition,
    isLoading,
    error: definitionError,
    refetch: getData,
  } = useGetCourseDefinitionQuery(parseInt(definitionId!), {
    skip: !definitionId,
  });

  const [updateCourseDefinition] = useUpdateCourseDefinitionMutation();

  const data = fetchedDefinition ?? CreateDefaultCourseDefinition();
  const [contents, setContents] = useState(data.contents ?? []);
  const initialContentsSnapshot = useRef<string>('');
  const [isSavePending, setIsSavePending] = useState(false);

  const contentLanguagesKey = getContentLanguages(contents).join(',');
  const formMeta = useMemo(
    () =>
      CourseFormMeta(
        courses,
        contentLanguagesKey ? contentLanguagesKey.split(',') : []
      ),
    [courses, contentLanguagesKey]
  );
  const form = useProactForm({formMeta});
  const {resetForm} = form;

  useEffect(() => {
    if (form.methods.getFieldState('defaultLanguage').error) {
      void form.methods.trigger('defaultLanguage');
    }
  }, [contentLanguagesKey, form.methods]);

  useEffect(() => {
    if (!fetchedDefinition) return;
    resetForm(MapCourseDefinitionResponseToCourseFormData(fetchedDefinition));
    setContents(fetchedDefinition.contents ?? []);
    initialContentsSnapshot.current = JSON.stringify(
      fetchedDefinition.contents ?? []
    );
  }, [fetchedDefinition, resetForm]);

  const contentsDirty =
    JSON.stringify(contents) !== initialContentsSnapshot.current;
  const isDirty = form.methods.formState.isDirty || contentsDirty;

  const {isBlocked, confirmLeave, cancelLeave} = useUnsavedChangesGuard(
    isDirty && !isSavePending
  );

  const extensionTabs = useExtensionTabs(
    ModuleEnum.Training,
    definitionId ? parseInt(definitionId) : 0
  );

  const isReadOnly = data.locked === true;
  const canEdit = trainingPerms.update && !isReadOnly;
  const activeView = (view ?? 'build') as CourseDefinitionViewType;

  const requestSave = async () => {
    if (!(await form.validateForm())) {
      return;
    }

    const contentError = validateCourseDefinitionContents(contents ?? []);
    if (contentError) {
      setAlert(contentError, 'error');
      return;
    }

    try {
      setIsSavePending(true);

      const model = MapCourseFormDataToCourseDefinitionRequest(
        {...toFormDataV2(data), ...form.getFieldValues()},
        contents ?? []
      );

      const response = await updateCourseDefinition(model).unwrap();

      setAlert(`Course ${response?.name} was successfully updated.`, 'success');
      void getData();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const {RenderedTabsComponent} = useUrlQueryTabV2<CourseDefinitionViewType>({
    view: (view ?? '') as CourseDefinitionViewType,
    defaultView: 'build',
    tabs: [
      {
        view: 'build',
        label: 'Build',
        component: (
          <BuildTab
            contents={contents}
            defaultLanguage={data.defaultLanguage ?? 'en'}
            readOnly={!canEdit}
            onContentsChange={setContents}
          />
        ),
      },
      {
        view: 'assign',
        label: 'Assign',
        component: (
          <>{data.id && <AssignmentRulesTab courseDefinitionId={data.id} />}</>
        ),
      },
      {
        view: 'roster',
        label: 'Roster',
        component: (
          <>{data.id && <ViewCourseDefinitionAssignments course={data} />}</>
        ),
      },
      {
        view: 'classes',
        label: 'Classes',
        component: (
          <>
            <ViewCourseClassrooms course={data} />
          </>
        ),
      },
      {
        view: 'results',
        label: 'Results',
        component: <ResultsTab course={data} />,
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: !trainingPerms.notifications,
        component: (
          <>
            <NotificationsModule
              entityId={data.id}
              notificationType={NotificationModuleEnum.CourseDefinition}
              includeDescendents={true}
            />
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: data.id && (
          <AuditModule
            entityId={String(data.id)}
            entityType={EntityIdentifierEnum.CourseDefinition}
          />
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view =>
      navigate(
        PathUtility.TrainingCourseView(definitionId as string, view as any),
        {replace: true}
      ),
  });

  useEffect(() => {
    if (courseListError) {
      setAlert(parseErrorMessage(courseListError), 'error');
    }
  }, [courseListError]);

  useEffect(() => {
    if (!courseListData) return;
    const remap =
      courseListData.data?.map(
        course => ({id: course.id, label: course.name} as SelectOption)
      ) ?? [];
    setCourses(remap);
  }, [courseListData]);

  useEffect(() => {
    if (definitionError) {
      setAlert(parseErrorMessage(definitionError), 'error');
    }
  }, [definitionError]);

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'course',
    courseName: data?.name,
  });

  const pills = CoursePillConfig(data?.active ?? true);

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box>
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
        />
        {isLoading ? (
          <Spinner loadingText="Loading...." />
        ) : definitionError || !fetchedDefinition ? (
          <Alert color="red" title="Error">
            Failed to load course. Please try again later.
          </Alert>
        ) : (
          <>
            <PageHeader>
              <PageHeader.Title icon={TrainingIcon}>
                Course {data.name}
              </PageHeader.Title>
              {data.internalIdentifier && (
                <PageHeader.Meta>{data.internalIdentifier}</PageHeader.Meta>
              )}
              <PageHeader.Badges>
                {pills.map((pill, index) => (
                  <PageHeader.Badge
                    key={index}
                    color={pill.color}
                    variant="light">
                    {pill.text}
                  </PageHeader.Badge>
                ))}
              </PageHeader.Badges>
              {canEdit && activeView === 'build' && (
                <PageHeader.Actions>
                  <PageHeader.Action
                    variant="save"
                    onClick={requestSave}
                    loading={isSavePending}>
                    Save
                  </PageHeader.Action>
                </PageHeader.Actions>
              )}
            </PageHeader>
            {contents.length === 0 && <ClassroomOnlyBanner canEdit={canEdit} />}
            <LockGuard
              id={data.id!}
              type={EntityIdentifierEnum.CourseDefinition}
              lockCache={data.locked}
              onUnlock={getData}
              renderMode="banner">
              <ProactForm form={form}>{RenderedTabsComponent}</ProactForm>
            </LockGuard>
          </>
        )}

        <ConfirmDialog
          opened={isBlocked}
          onClose={cancelLeave}
          onConfirm={confirmLeave}
          confirmationButtonColor="red"
          title="Leave without saving?"
          body="You have unsaved changes on this course. If you leave now, they will be lost."
          buttonTextYes="Leave"
          buttonTextNo="Stay"
        />
      </Box>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingCourseView(':definitionId', ':view'),
  element: <ViewCourseDefinitionPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default ViewCourseDefinitionPage;
